/**
 * Picker de miembros del community map (buscar por nombre / mail / rol / área
 * y elegir uno). Auditoría 18/09/2026: el mismo dialog —input con lupa, lista
 * de 50, fila con nombre + (status) + "rol · área", ícono +— estaba copiado en
 * GovernancePanel, PartnershipsGovernancePage y TalentMapGovernancePage.
 *
 * `children` es el slot ARRIBA del buscador para el selector propio del módulo
 * (alcance de edición en Partnerships, rol en el Mapa de Talento).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Plus, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { foldKey } from '../../../../shared/text-normalize';

export interface PickableMember {
  email: string;
  name: string;
  role?: string;
  area?: string;
  team?: string;
  status?: string;
}

export function MemberPickerDialog<M extends PickableMember>({
  open, onOpenChange, title, description, searchPlaceholder, noResultsText, members, loading = false, exclude, onPick, children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  searchPlaceholder: string;
  noResultsText: string;
  members: M[];
  loading?: boolean;
  /** Emails (en minúsculas) que ya están en el roster: no se ofrecen. */
  exclude: ReadonlySet<string>;
  onPick: (member: M) => void;
  children?: ReactNode;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    // sin tildes ni mayúsculas en las dos puntas: "lucia" encuentra a Lucía
    const q = foldKey(search);
    return members
      .filter((m) => !exclude.has(m.email.toLowerCase()))
      .filter((m) => !q || [m.name, m.email, m.role, m.area].some((v) => foldKey(v).includes(q)))
      .slice(0, 50);
  }, [members, exclude, search]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setSearch(''); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input autoFocus placeholder={searchPlaceholder} className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-tour="member-search" />
        </div>
        <ScrollArea className="-mx-2 h-72 px-2">
          {loading ? (
            <div className="flex items-center justify-center py-10"><WinclapLoader /></div>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">{noResultsText}</p>
          ) : (
            <div className="space-y-1">
              {filtered.map((m) => (
                <button key={m.email} type="button" onClick={() => onPick(m)} data-tour="member-row" className="flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-muted">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {m.name}
                      {m.status && m.status !== 'Activo' && <span className="ml-2 text-xs text-amber-600">({m.status})</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{[m.role, m.area || m.team].filter(Boolean).join(' · ') || m.email}</div>
                  </div>
                  <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
